import type { ReferenceDto } from 'cadenza/api-client/dto/reference-dto';
import type { WorldBoxDto } from 'cadenza/api-client/dto/world-box-dto';
import type { CadenzaStyle } from 'cadenza/map/style/style';
import type { Truncated } from 'cadenza/model/truncated.gen';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import type { ColumnIdentificationDto } from 'cadenza/workbook/selection/model/column-origin-dto';

/** @see net/disy/cadenza/workbook/web/map/timeline/dto/TimelinesDto.java */
export interface TimelinesDto {
  readonly timelines: TimelineDto[];
  readonly styles: TimelineStyleDto[];
  readonly layerSettings: TimelineLayerSettingsDto;
  readonly staticPoints: StaticPointsDto[];
  readonly maximumDisplayPointCount: number;
  readonly warnPointCount: number;
  readonly totalPointCount: number;
  readonly layersWithTruncatedResult: LayerWithFeatureLimitDto[];
  readonly extent: WorldBoxDto;
  readonly timeScaleTicks: number[];
  readonly originByLayerId: Record<LayerId, ColumnIdentificationDto>;
  readonly srs: string;
}

/** @see net/disy/cadenza/workbook/web/map/timeline/dto/TimelineDto.java */
export interface TimelineDto {
  readonly layerId: DataViewId;
  readonly timelineId: string;
  readonly layerName: string;
  readonly points: TimelinePointDto[];
  readonly visible: boolean;
  readonly linesVisible: boolean;
}

/** @see net/disy/cadenza/workbook/web/map/timeline/dto/TimelinePointDto.java */
export type TimelinePointDto = [
  number, // x
  number, // y
  ReferenceDto, // styleReference
  number, // time
  string, // featureId
  string[], // featureIdValues
  number, // renderOrder
  number, // speed
  number, // calculated speed
  number?, // satelliteCount
];

/** @see net/disy/cadenza/workbook/web/map/timeline/dto/TimelineStyleDto.java */
// We store the styles in Redux, image is of type HTMLImageElement which does not work well with Redux, but it's fine cause we don't use it.
export interface TimelineStyleDto extends Omit<CadenzaStyle, 'image'> {
  hashCode: number;
}

/** @see net/disy/cadenza/workbook/web/map/timeline/dto/TimelineLayerSettingsDto.java */
interface TimelineLayerSettingsDto {
  readonly [key: string]: TimelineLayerSettingsEntryDto;
}

/** @see net/disy/cadenza/workbook/web/map/timeline/dto/TimelineLayerSettingsEntryDto.java */
interface TimelineLayerSettingsEntryDto {
  readonly fading: TimelineLayerFadingSettingsDto;
  readonly highlightLastPoint?: boolean;
}

/** @see net/disy/cadenza/workbook/web/map/timeline/dto/TimelineLayerFadingSettingsDto.java */
interface TimelineLayerFadingSettingsDto {
  readonly type: TimelineLayerFadingType;
  readonly transition: TimelineLayerFadingTransition;
  readonly duration: TimelineLayerFadingSettingsDurationDto;
  readonly count: number;
}

/** @see net/disy/cadenza/workbook/web/map/timeline/dto/TimelineLayerFadingSettingsDurationDto.java */
export interface TimelineLayerFadingSettingsDurationDto {
  readonly milliseconds: number;
  readonly configurationUnit: TimelineLayerFadingDurationUnit;
}

/** @see net/disy/cadenza/workbook/web/map/timeline/dto/StaticPointsDto.java */
interface StaticPointsDto {
  readonly layerId: DataViewId;
  points: TimelinePointDto[];
}

/** @see net/disy/cadenza/workbook/web/map/timeline/dto/LayerWithFeatureLimitDto.java */
export interface LayerWithFeatureLimitDto {
  /** Truncated enum type */
  readonly truncated: Truncated;
  /** ID of the layer */
  readonly id: DataViewId;
  /** Name of the layer */
  readonly name: string;
  /** Value of the row count limit that was reached */
  readonly rowCountLimit: number;
  /** Value of the geometry vertex count limit that was reached */
  readonly geometryVertexCountLimit: number;
}

/**
 * @see net.disy.gis.layer.configuration.atoms.content.timeline.IFading
 */
export const TimelineLayerFadingType = {
  NO_FADING: 'NoFading',
  AFTER_DURATION_FADING: 'AfterDurationFading',
  AFTER_COUNT_FADING: 'AfterCountFading',
} as const;

export type TimelineLayerFadingType = (typeof TimelineLayerFadingType)[keyof typeof TimelineLayerFadingType];

/**
 * @see net.disy.gis.layer.configuration.atoms.content.timeline.FadingTransition.java
 */
export const TimelineLayerFadingTransition = {
  NONE: 'NONE',
  TRANSPARENCY: 'TRANSPARENCY',
  SIZE: 'SIZE',
  TRANSPARENCY_AND_SIZE: 'TRANSPARENCY_AND_SIZE',
} as const;

export type TimelineLayerFadingTransition = keyof typeof TimelineLayerFadingTransition;

/**
 * A subset of `java.util.concurrent.TimeUnit`
 *
 * @see net.disy.gis.layer.configuration.atoms.content.timeline.AfterDurationFading
 */
export const TimelineLayerFadingDurationUnit = {
  MILLISECONDS: 'MILLISECONDS',
  SECONDS: 'SECONDS',
  MINUTES: 'MINUTES',
  HOURS: 'HOURS',
  DAYS: 'DAYS',
} as const;

export type TimelineLayerFadingDurationUnit = keyof typeof TimelineLayerFadingDurationUnit;
